/**
 * Tenants Hook
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantService, CreateTenantDto } from '../services/tenantService';
import type { TenantStatus } from '../types';

const QUERY_KEYS = {
  stats: ['platform', 'stats'],
  tenants: ['platform', 'tenants'],
  tenant: (id: string) => ['platform', 'tenants', id],
};

export function usePlatformStats() {
  return useQuery({
    queryKey: QUERY_KEYS.stats,
    queryFn: tenantService.getStats,
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useTenants() {
  return useQuery({
    queryKey: QUERY_KEYS.tenants,
    queryFn: tenantService.getAll,
  });
}

export function useTenant(id: string) {
  return useQuery({
    queryKey: QUERY_KEYS.tenant(id),
    queryFn: () => tenantService.getById(id),
    enabled: !!id,
  });
}

export function useCreateTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dto: CreateTenantDto) => tenantService.create(dto),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenants });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stats });
    },
  });
}

export function useUpdateTenantStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: TenantStatus }) =>
      tenantService.updateStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenants });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenant(id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stats });
    },
  });
}

export function useDeleteTenant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenants });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stats });
    },
  });
}

export function useCreateEnvironment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      tenantId,
      name,
      adminUser,
    }: {
      tenantId: string;
      name: string;
      adminUser?: CreateTenantDto['adminUser'];
    }) => tenantService.createEnvironment(tenantId, name, adminUser),
    onSuccess: (_, { tenantId }) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.tenant(tenantId) });
    },
  });
}
